import { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { Context } from "../../../models/context";
import { GetProjects, PostProjects } from "../../../services/projects";
import { Project } from "../../../models/project";
import { BaseScopes } from "../../../models/scopes";
import ProjectBox from "../../../components/projectBox/ProjectBox";
import Help from "../../../components/help/Help";
import ProjectSettingsModal from "../../../components/projectSettingsModal/ProjectSettingsModal";
import PermissionGuard from "../../../components/permissionGuard/PermissionGuard";

import "../../../components/help/help.css";
import "./project-dashboard.css";
import { useAlerts } from "../../../contexts/AlertContext";

export default function ProjectDashboard() {
    const context = useContext(Context);
    const navigate = useNavigate();
    const getProjects = new GetProjects();
    const postProjects = new PostProjects();

    const [projects, setProjects] = useState<Project[]>([]);
    const [creatingProject, setCreatingProject] = useState(false);

    const {addError} = useAlerts();

    useEffect(() => {
        if (!context.user) {
            navigate("/login");
            return;
        }

        getProjects.request(context.user!).then(r => setProjects(r.map(Project.ensureInstance)));
    }, [context.user, navigate]);

    const updateProject = (project: Project, newProject: Project) => {
        console.log("Updating project to be ", newProject.toJson());
        const i = projects.findIndex(p => p.name === project.name);
        if (i !== -1) setProjects(ps => [...ps.slice(0, i), newProject, ...ps.slice(i + 1)]);
        else setProjects(ps => [...ps, newProject]);

        postProjects.request(newProject.toJson(), context.user!).catch(err => {
            addError(err as Error, "Error updating or creating project");
            console.error("Error updating or creating project: ", err);
        });
    };

    const projectBoxes = useMemo(() => {
        return [...projects].sort((a, b) => {
            if (a.completed && !b.completed) return 1;
            if (!a.completed && b.completed) return -1;
            return a.name.localeCompare(b.name);
        })
        .map((p, i) => <ProjectBox project={p} updateProject={updateProject} key={"project-" + i} />);
    }, [projects]);

    return (
        <div id="project-page">
            <div id="project-wrapper" className="container">
                <div id="project-header">
                    <div className="help-title">
                        <h1>Project List</h1>
                        <Help title="Projects" color="var(--green-medium)" className="help-button">
                            <p>This is the Project List dashboard, where you can view and manage all of your projects.</p>
                            <p>Click on a project to view its details, or click the "New Project" button to create a new one.
                                Edit a project by clicking on the edit-icon on the right side.
                            </p>
                            <h3>Project Details</h3>
                            <div id="project-details">
                                <p id="project-details-item"><b>Count:</b> How many hints are in the project.</p>
                                <p id="project-details-item"><b>Regeneration:</b> Does the project get more hints automatically from GoFetch and Harvester?</p>
                                <p id="project-details-item"><b>Threshold:</b> The number of hints where an attempt to generate more is made or the mailing list is notified.</p>
                                <p id="project-details-item"><b>Group:</b> Which groups can access this project.</p>
                                <p id="project-details-item"><b>Mailing list:</b> Users that will be notified when the threshold is reached.</p>
                            </div>
                        </Help>
                    </div>
                </div>
                <div id="project-accordion">
                    <div id="project-page-wrapper">
                        {projectBoxes.length > 0 && projectBoxes}
                    </div>

                    {projectBoxes.length === 0 &&
                        <div id="no-projects">
                            <p>No projects yet. Click "New Project" to get started!</p>
                        </div>}

                    {creatingProject && <ProjectSettingsModal
                        isOpen={creatingProject}
                        creating={true}
                        onClose={() => setCreatingProject(false)}
                        project={new Project("New Project", 0, false, "", false, false, 1, new Date(), [])}
                        updateProject={(project, newProject) => {
                            updateProject(project, newProject);
                            setCreatingProject(false);
                        }}
                    />}

                </div>
                <section className="actions" id="project-actions">
                    <h3 id="project-count">Total Projects: {projects.length}</h3>
                    <PermissionGuard requiredScopes={[BaseScopes.MAINTAIN]}>
                        <input type="button" value="New Project" className="section-button" onClick={() => setCreatingProject(true)} />
                    </PermissionGuard>
                </section>

            </div>

        </div>

    );
}
